import Breadcrumb from "@/components/Breadcrumb";
import { Box, Button, Tab, Tabs, TextField } from "@mui/material";
import MasterCard from "@/components/master/MasterCard";
import { useEffect, useMemo, useState } from "react";
import type { Master } from "@/api/masterService";
import MasterTable from "@/components/master/MasterTable";
import { useSystemMasterData } from "@/hooks/useSystemMasterData";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { usePermissions } from "@/hooks/usePermissions";
import SaveMasterModal from "@/components/master/SaveMasterModal";
import { DefaultSecondaryButton } from "@/styles/globalstyles";
import { Add } from "@mui/icons-material";
import { getAxiosErrorMessage } from "@/utils/healper";
import { useToast } from "@/hooks/useToast";
import { ErrorAlert } from "@/utils/alert";

const SystemMasterManagement = () => {
  const canEdit = usePermissions().canEdit("master.system");
  const { showToast } = useToast();
  const [tabValue, setTabValue] = useState<number>(0);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [openSaveModal, setOpenSaveModal] = useState<boolean>(false);
  const [selectedMaster, setSelectedMaster] = useState<Master | undefined>(
    undefined,
  );

  const {
    rows,
    countActive,
    countInactive,
    systemMasterTabs,
    createMaster,
    updateMaster,
    isLoading,
    isError,
  } = useSystemMasterData(tabValue);

  const filteredRows = useMemo(() => {
    if (!searchFilter) {
      return rows;
    }

    return rows?.filter((row: Master) =>
      row.name?.toLowerCase().includes(searchFilter?.toLowerCase()),
    );
  }, [rows, searchFilter]);

  useQueryHandler({
    isError: isError,
    errorMessage: "เกิดข้อผิดพลาดในการโหลดข้อมูล",
  });

  useQueryHandler({
    isFetching: createMaster?.isPending || updateMaster?.isPending,
  });

  useEffect(() => {
    document.title = "System Master | AMPA";
  }, []);

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleClickAddSystemMaster = () => {
    setSelectedMaster(undefined);
    setOpenSaveModal(true);
  };

  const handleAddMaster = (data: Master) => {
    createMaster?.mutate(data, {
      onSuccess: () => {
        showToast("เพิ่ม Master สำเร็จ", "success");
      },
      onError: (error) => {
        ErrorAlert("Error", getAxiosErrorMessage(error));
        console.log("Error creating Master:", error);
      },
    });
  };

  const handleEditMaster = (data: Master) => {
    updateMaster?.mutate(data, {
      onSuccess: () => {
        showToast("แก้ไข Master สำเร็จ", "success");
      },
      onError: (error) => {
        ErrorAlert("Error", getAxiosErrorMessage(error));
        console.log("Error updating Master:", error);
      },
    });
  };

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb current="master.system" />
      <Box mt={1}>
        <MasterCard
          titleActive={`ACTIVE (${
            systemMasterTabs.find((tab) => tab.value === tabValue)?.label
          })`}
          titleInactive={`INACTIVE (${
            systemMasterTabs.find((tab) => tab.value === tabValue)?.label
          })`}
          countActive={countActive}
          countInactive={countInactive}
        />
        <Box>
          <Box mt={2} sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={tabValue} onChange={handleChangeTab} aria-label="tabs">
              {systemMasterTabs.map((tab, index) => (
                <Tab key={index} label={tab.label} />
              ))}
            </Tabs>
          </Box>
          <Box sx={{ p: 1, borderRadius: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  label="ค้นหา Master"
                  variant="outlined"
                  size="small"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  sx={{
                    mt: 2,
                    mb: 1,
                    width: "30%",
                    "& .MuiInputLabel-root": {
                      fontSize: "0.8rem",
                      color: "#666666",
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#cccccc",
                      },
                      "&:hover fieldset": {
                        borderColor: "#999999",
                      },
                    },
                  }}
                />
              </Box>
              <Box gap={1} display="flex">
                <Button
                  variant="outlined"
                  sx={{
                    ...DefaultSecondaryButton,
                    mt: 2,
                    alignItems: "center",
                  }}
                  onClick={() => handleClickAddSystemMaster()}
                  disabled={!canEdit}
                >
                  <Add /> เพิ่ม{" "}
                  {
                    systemMasterTabs.find((tab) => tab.value === tabValue)
                      ?.label
                  }
                </Button>
              </Box>
            </Box>
            <MasterTable
              data={filteredRows}
              isLoading={isLoading}
              handleEdit={(row) => () => {
                console.log("row", row);
                setSelectedMaster(row);
                setOpenSaveModal(true);
              }}
              canEdit={canEdit}
            />
          </Box>
        </Box>
      </Box>

      <SaveMasterModal
        open={openSaveModal}
        handleClose={() => {
          setOpenSaveModal(false);
          setSelectedMaster(undefined);
        }}
        selectedMaster={selectedMaster}
        handleAddMaster={(data) => {
          handleAddMaster(data);
        }}
        handleEditMaster={(data) => {
          handleEditMaster(data);
        }}
      />
    </Box>
  );
};

export default SystemMasterManagement;
